import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Check, X, Clock, RefreshCw, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const ShipmentRequest = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectDialog, setRejectDialog] = useState({ open: false, requestId: null, categoryRequestId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    source_design_name: '',
    target_design_name: '',
    color: '',
    quantity: '',
    remarks: ''
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getShipmentRequests();
      setRequests(data.requests || []);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAccept = async (requestId) => {
    try {
      await greigeStorageApi.acceptShipmentRequest(requestId);
      toast.success('Request accepted');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      if (rejectDialog.categoryRequestId) {
        // Bulk reject all children of a parent SR
        await greigeStorageApi.rejectShipmentRequestGroup(rejectDialog.categoryRequestId, rejectReason);
        toast.success('All items rejected');
      } else {
        await greigeStorageApi.rejectShipmentRequest(rejectDialog.requestId, rejectReason);
        toast.success('Request rejected');
      }
      setRejectDialog({ open: false, requestId: null, categoryRequestId: null });
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleGroupAccept = async (grp) => {
    try {
      if (grp.is_group && grp.items.length > 1) {
        await greigeStorageApi.acceptShipmentRequestGroup(grp.parent_id);
        toast.success(`Accepted all items in ${grp.parent_id}`);
      } else {
        await greigeStorageApi.acceptShipmentRequest(grp.items[0].request_id);
        toast.success('Request accepted');
      }
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.source_design_name || !newRequest.color || !newRequest.quantity) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await greigeStorageApi.createShipmentRequest({
        ...newRequest,
        quantity: parseInt(newRequest.quantity)
      });
      toast.success('Request created');
      setCreateDialog(false);
      setNewRequest({ source_design_name: '', target_design_name: '', color: '', quantity: '', remarks: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      accepted: { color: 'bg-green-100 text-green-700', icon: Check },
      rejected: { color: 'bg-red-100 text-red-700', icon: X },
      pick_slip_generated: { color: 'bg-blue-100 text-blue-700', icon: FileText }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'pending') return r.status === 'pending';
    if (activeTab === 'accepted') return r.status === 'accepted' || r.status === 'pick_slip_generated';
    if (activeTab === 'all') return true;
    return true;
  });

  const counts = {
    pending: requests.filter(r => r.status === 'pending').length,
    accepted: requests.filter(r => r.status === 'accepted' || r.status === 'pick_slip_generated').length,
    all: requests.length,
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCreateDialog(true)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Request
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchRequests}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending" data-testid="gs-sr-tab-pending">
              Pending
              {counts.pending > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-white border-0 h-5 min-w-5 px-1.5 text-xs">
                  {counts.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="accepted" data-testid="gs-sr-tab-accepted">
              Accepted
              {counts.accepted > 0 && (
                <Badge className="ml-2 bg-green-600 text-white border-0 h-5 min-w-5 px-1.5 text-xs">
                  {counts.accepted}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="gs-sr-tab-all">
              All
              {counts.all > 0 && (
                <Badge className="ml-2 bg-gray-500 text-white border-0 h-5 min-w-5 px-1.5 text-xs">
                  {counts.all}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <Card className="border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Requests</h3>
                  <p className="text-[#5A5F6E]">No shipment requests in this category</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {Object.values(
                  filteredRequests.reduce((acc, req) => {
                    const parentId = req.category_request_id || req.request_id;
                    if (!acc[parentId]) {
                      acc[parentId] = {
                        parent_id: parentId,
                        is_group: !!req.category_request_id,
                        items: [],
                        priority: req.priority,
                        requested_by_name: req.requested_by_name,
                        approved_by_name: req.approved_by_name,
                        created_at: req.created_at,
                        remarks: req.remarks,
                      };
                    }
                    acc[parentId].items.push(req);
                    return acc;
                  }, {})
                )
                  .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                  .map((grp) => {
                    const totalQty = grp.items.reduce((s, i) => s + (i.quantity || 0), 0);
                    const statuses = [...new Set(grp.items.map((i) => i.status))];
                    const rollup = statuses.length === 1
                      ? statuses[0]
                      : (statuses.includes('pending') ? 'pending' : statuses[0]);
                    const pendingCount = grp.items.filter((i) => i.status === 'pending').length;
                    const acceptedCount = grp.items.filter((i) =>
                      i.status === 'accepted' || i.status === 'pick_slip_generated'
                    ).length;

                    return (
                      <Card
                        key={grp.parent_id}
                        className="border-[#E5DFD0]"
                        data-testid={`request-group-${grp.parent_id}`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              <CardTitle className="text-lg font-mono">{grp.parent_id}</CardTitle>
                              {grp.items.length > 1 && (
                                <Badge variant="outline" className="text-xs">
                                  {grp.items.length} items
                                </Badge>
                              )}
                              {grp.priority && grp.priority !== 'normal' && (
                                <Badge className={
                                  grp.priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-300 border' :
                                  grp.priority === 'high' ? 'bg-orange-100 text-orange-700 border-orange-300 border' :
                                  'bg-gray-100 text-gray-700 border-gray-300 border'
                                }>
                                  {grp.priority.toUpperCase()}
                                </Badge>
                              )}
                            </div>
                            {getStatusBadge(rollup)}
                          </div>
                        </CardHeader>

                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
                            <div>
                              <p className="text-xs text-[#5A5F6E]">Items</p>
                              <p className="font-medium text-[#1A1F2E]">{grp.items.length}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#5A5F6E]">Total Qty</p>
                              <p className="font-medium text-[#1A1F2E]">{totalQty}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#5A5F6E]">Pending / Accepted</p>
                              <p className="font-medium text-[#1A1F2E]">
                                <span className="text-yellow-700">{pendingCount}</span>
                                {' / '}
                                <span className="text-green-700">{acceptedCount}</span>
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[#5A5F6E]">Approved By</p>
                              <p className="font-medium text-[#1A1F2E]">{grp.approved_by_name || grp.requested_by_name || '-'}</p>
                            </div>
                          </div>

                          {grp.remarks && (
                            <p className="text-sm text-[#5A5F6E] mb-3">Remarks: {grp.remarks}</p>
                          )}

                          {/* Items table (collapsible for big groups) */}
                          <details className="mb-3 border rounded bg-gray-50" {...(grp.items.length <= 5 ? { open: true } : {})}>
                            <summary className="cursor-pointer text-sm font-medium px-3 py-2 hover:bg-gray-100 select-none">
                              {grp.items.length === 1 ? 'Show item details' : `Show all ${grp.items.length} items`}
                            </summary>
                            <div className="overflow-x-auto border-t">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-100 text-left">
                                    <th className="p-2">#</th>
                                    <th className="p-2">Source Design</th>
                                    <th className="p-2">Target Design</th>
                                    <th className="p-2">Color</th>
                                    <th className="p-2 text-right">Qty</th>
                                    <th className="p-2">Status</th>
                                    <th className="p-2"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {grp.items.map((item, idx) => (
                                    <tr
                                      key={item.request_id}
                                      className="border-t hover:bg-white"
                                      data-testid={`request-item-${item.request_id}`}
                                    >
                                      <td className="p-2">{item.item_index != null ? item.item_index + 1 : idx + 1}</td>
                                      <td className="p-2">{item.source_design_name}</td>
                                      <td className="p-2">{item.target_design_name || '-'}</td>
                                      <td className="p-2">{item.color}</td>
                                      <td className="p-2 text-right font-semibold">{item.quantity}</td>
                                      <td className="p-2">{getStatusBadge(item.status)}</td>
                                      <td className="p-2">
                                        {item.status === 'accepted' && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/sections/greige-storage/pick-slip?request=${item.request_id}`)}
                                            className="h-7 text-xs"
                                            data-testid={`generate-slip-${item.request_id}`}
                                          >
                                            <FileText className="w-3 h-3 mr-1" />
                                            Pick Slip
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>

                          <div className="text-xs text-[#5A5F6E] mb-3">
                            Created: {new Date(grp.created_at).toLocaleString('en-IN')}
                            {grp.requested_by_name && <> • Requested by {grp.requested_by_name}</>}
                          </div>

                          {pendingCount > 0 && (
                            <div className="flex gap-3 flex-wrap">
                              <Button
                                onClick={() => handleGroupAccept(grp)}
                                className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
                                data-testid={`accept-group-${grp.parent_id}`}
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Accept {grp.is_group && grp.items.length > 1 ? `All ${pendingCount} Item(s)` : ''}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() =>
                                  setRejectDialog({
                                    open: true,
                                    requestId: grp.is_group ? null : grp.items[0].request_id,
                                    categoryRequestId: grp.is_group ? grp.parent_id : null,
                                  })
                                }
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`reject-group-${grp.parent_id}`}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Reject {grp.is_group && grp.items.length > 1 ? 'All' : ''}
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, requestId: null, categoryRequestId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Input
              id="reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, requestId: null, categoryRequestId: null })}>
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Shipment Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Source Design Name *</Label>
              <Input
                value={newRequest.source_design_name}
                onChange={(e) => setNewRequest(prev => ({ ...prev, source_design_name: e.target.value }))}
                placeholder="Current greige design..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Target Design Name</Label>
              <Input
                value={newRequest.target_design_name}
                onChange={(e) => setNewRequest(prev => ({ ...prev, target_design_name: e.target.value }))}
                placeholder="Design to be produced..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Color *</Label>
              <Input
                value={newRequest.color}
                onChange={(e) => setNewRequest(prev => ({ ...prev, color: e.target.value }))}
                placeholder="Bundle color..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input
                type="number"
                value={newRequest.quantity}
                onChange={(e) => setNewRequest(prev => ({ ...prev, quantity: e.target.value }))}
                placeholder="Number of bundles..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={newRequest.remarks}
                onChange={(e) => setNewRequest(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Additional notes..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRequest} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]">
              Create Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentRequest;
